import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Info, KeyRound, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminMerchantsGetAdminMerchantsIdGuidQueryKey,
  useAdminMerchantsDeleteAdminMerchantsIdGuidAdyenApiKey,
  useAdminMerchantsPutAdminMerchantsIdGuidAdyenApiKey,
  useAdminMerchantsPutAdminMerchantsIdGuidLiveUrlPrefix,
} from '@/lib/api/admin-merchants/admin-merchants';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHasRole } from '@/lib/auth/useAuth';

interface Props {
  merchantId: string;
  adyenApiKeySet: boolean;
  adyenLiveUrlPrefix: string | null | undefined;
}

const KeySchema = z.object({
  adyenApiKey: z
    .string()
    .min(8, 'Key looks too short — Adyen keys are typically 100+ chars.')
    .max(2048),
});
type KeyForm = z.infer<typeof KeySchema>;

const PrefixSchema = z.object({
  liveUrlPrefix: z
    .string()
    .max(64)
    .regex(/^[A-Za-z0-9_-]*$/, 'Only letters, numbers, hyphen and underscore.')
    .optional()
    .or(z.literal('')),
});
type PrefixForm = z.infer<typeof PrefixSchema>;

/**
 * SuperAdmin-only card for managing the per-merchant Adyen credentials.
 * The plaintext key is never echoed back from the backend; this card only
 * shows whether one is set, and gates rotate/clear behind explicit
 * confirmation dialogs. Lower-rank admins see a disabled read-only view
 * with a "SuperAdmin only" badge — the backend enforces this regardless.
 */
export function AdyenCredentialsCard({
  merchantId,
  adyenApiKeySet,
  adyenLiveUrlPrefix,
}: Props) {
  const isSuperAdmin = useHasRole('SuperAdmin');
  const [setKeyOpen, setSetKeyOpen] = useState(false);
  const [prefixOpen, setPrefixOpen] = useState(false);

  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const invalidateMerchant = () =>
    queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsIdGuidQueryKey(merchantId) });

  const setKeyMutation = useAdminMerchantsPutAdminMerchantsIdGuidAdyenApiKey({
    mutation: {
      onSuccess: async () => {
        await invalidateMerchant();
        toast.success(adyenApiKeySet ? 'Adyen key rotated.' : 'Adyen key set.');
        setSetKeyOpen(false);
      },
      onError: () => toast.error('Could not save Adyen key.'),
    },
  });

  const clearKeyMutation = useAdminMerchantsDeleteAdminMerchantsIdGuidAdyenApiKey({
    mutation: {
      onSuccess: async () => {
        await invalidateMerchant();
        toast.success('Adyen key cleared. Falling back to client / global key.');
      },
      onError: () => toast.error('Could not clear Adyen key.'),
    },
  });

  const prefixMutation = useAdminMerchantsPutAdminMerchantsIdGuidLiveUrlPrefix({
    mutation: {
      onSuccess: async () => {
        await invalidateMerchant();
        toast.success('Live URL prefix updated.');
        setPrefixOpen(false);
      },
      onError: () => toast.error('Could not save live URL prefix.'),
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            Adyen credentials
          </CardTitle>
          <CardDescription>
            Per-merchant overrides used for live and test API calls. Falls back to the parent client&apos;s key when
            unset.
          </CardDescription>
        </div>
        {!isSuperAdmin && (
          <Badge variant="outline" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            SuperAdmin only
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
          <div>
            <p className="text-sm font-medium">Adyen API key</p>
            <p className="text-xs text-muted-foreground">
              {adyenApiKeySet
                ? 'A merchant-level override is active.'
                : 'No override — calls route through the client key.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {adyenApiKeySet ? (
              <Badge variant="success">Set</Badge>
            ) : (
              <Badge variant="outline">Not set</Badge>
            )}
            {isSuperAdmin && (
              <>
                <Button size="sm" variant="outline" onClick={() => setSetKeyOpen(true)}>
                  {adyenApiKeySet ? 'Rotate' : 'Set'}
                </Button>
                {adyenApiKeySet && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Clear this merchant’s Adyen API key?',
                        description:
                          'New /proxy calls scoped to this merchant will fall back to the client-level Adyen key. Existing calls in flight are unaffected.',
                        confirmLabel: 'Clear key',
                        variant: 'destructive',
                      });
                      if (ok) clearKeyMutation.mutate({ id: merchantId });
                    }}
                    disabled={clearKeyMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
          <div>
            <p className="text-sm font-medium">Live URL prefix</p>
            <p className="font-mono text-xs text-muted-foreground">
              {adyenLiveUrlPrefix ? adyenLiveUrlPrefix : '— (test mode)'}
            </p>
          </div>
          {isSuperAdmin && (
            <Button size="sm" variant="outline" onClick={() => setPrefixOpen(true)}>
              {adyenLiveUrlPrefix ? 'Edit' : 'Set'}
            </Button>
          )}
        </div>

        <div
          className="flex items-start gap-3 rounded-md border border-border bg-muted/30 px-3 py-3"
          data-testid="merchant-webhook-hmac-callout"
        >
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium">Adyen webhook HMAC key is now platform-wide.</p>
            <p className="text-xs text-muted-foreground">
              Set it once on the Settings page; this merchant no longer stores its own key.
            </p>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              Open settings
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </CardContent>

      <SetKeyDialog
        open={setKeyOpen}
        onOpenChange={setSetKeyOpen}
        rotating={adyenApiKeySet}
        pending={setKeyMutation.isPending}
        onSubmit={(values) => setKeyMutation.mutate({ id: merchantId, data: { adyenApiKey: values.adyenApiKey } })}
      />

      <SetPrefixDialog
        open={prefixOpen}
        onOpenChange={setPrefixOpen}
        currentValue={adyenLiveUrlPrefix ?? ''}
        pending={prefixMutation.isPending}
        onSubmit={(values) =>
          prefixMutation.mutate({
            id: merchantId,
            data: { liveUrlPrefix: values.liveUrlPrefix?.trim() ? values.liveUrlPrefix.trim() : null },
          })
        }
      />
    </Card>
  );
}

interface SetKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rotating: boolean;
  pending: boolean;
  onSubmit: (values: KeyForm) => void;
}

function SetKeyDialog({ open, onOpenChange, rotating, pending, onSubmit }: SetKeyDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<KeyForm>({
    resolver: zodResolver(KeySchema),
    defaultValues: { adyenApiKey: '' },
  });

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{rotating ? 'Rotate Adyen API key' : 'Set Adyen API key'}</DialogTitle>
          <DialogDescription>
            Stored encrypted at rest; never echoed back. {rotating && 'Rotating replaces the previous value immediately.'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((values) => {
            onSubmit(values);
          })}
          className="space-y-4"
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="adyen-key">Plaintext API key</Label>
            <Input
              id="adyen-key"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              placeholder="AQE…"
              {...register('adyenApiKey')}
            />
            {errors.adyenApiKey && <p className="text-xs text-destructive">{errors.adyenApiKey.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || pending}>
              {pending ? 'Saving…' : rotating ? 'Rotate key' : 'Save key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SetPrefixDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentValue: string;
  pending: boolean;
  onSubmit: (values: PrefixForm) => void;
}

function SetPrefixDialog({ open, onOpenChange, currentValue, pending, onSubmit }: SetPrefixDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PrefixForm>({
    resolver: zodResolver(PrefixSchema),
    values: { liveUrlPrefix: currentValue },
  });

  const close = (next: boolean) => {
    if (!next) reset({ liveUrlPrefix: currentValue });
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Live URL prefix</DialogTitle>
          <DialogDescription>
            Adyen live merchant URL prefix (e.g. <span className="font-mono">1797a841fbb37ca7-DealerWorks</span>). Leave
            blank to clear and stay on the test base URL.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="live-prefix">Prefix</Label>
            <Input
              id="live-prefix"
              autoComplete="off"
              spellCheck={false}
              placeholder="1797a841fbb37ca7-DealerWorks"
              {...register('liveUrlPrefix')}
            />
            {errors.liveUrlPrefix && <p className="text-xs text-destructive">{errors.liveUrlPrefix.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || pending}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

